/**
 * Orchestra AI Platform - Edge Functions Helper
 * 
 * Helper functions for calling Supabase Edge Functions
 * 
 * @fileoverview Edge Functions API wrapper with authentication
 */

import { supabase } from './supabase';

// Use environment variable or fallback to your Supabase project URL
// Support both react-scripts (REACT_APP_*) and Vite (VITE_*) prefixes
const getEnvVar = (key) => {
  if (typeof process !== 'undefined' && process.env) {
    return process.env[key];
  }
  if (typeof import.meta !== 'undefined' && import.meta.env) {
    return import.meta.env[key];
  }
  return undefined;
};

const FUNCTIONS_BASE_URL = getEnvVar('REACT_APP_SUPABASE_FUNCTIONS_URL') || 
  getEnvVar('VITE_SUPABASE_FUNCTIONS_URL') || 
  (getEnvVar('REACT_APP_SUPABASE_URL') || getEnvVar('VITE_SUPABASE_URL')
    ? `${getEnvVar('REACT_APP_SUPABASE_URL') || getEnvVar('VITE_SUPABASE_URL')}/functions/v1`
    : 'https://eximyshafkuyehkiotuf.supabase.co/functions/v1');

/**
 * Call Edge Function with authentication
 * @param {string} functionName - Function name (ai-proxy, api-keys, etc.)
 * @param {Object} body - Request body
 * @param {Object} options - Additional options
 * @returns {Promise<Object>} Response data
 */
export async function callEdgeFunction(functionName, body = {}, options = {}) {
  try {
    // Get current session
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError) {
      throw new Error('Session error: ' + sessionError.message);
    }
    
    if (!session) {
      throw new Error('Not authenticated. Please log in.');
    }

    const url = `${FUNCTIONS_BASE_URL}/${functionName}`;
    
    const response = await fetch(url, {
      method: options.method || 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
        ...options.headers,
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ 
        error: response.statusText,
        status: response.status,
      }));
      throw new Error(error.error || `Function call failed: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error(`Error calling ${functionName}:`, error);
    throw error;
  }
}

/**
 * Call AI Proxy function
 * @param {Object} request - Proxy request
 * @param {('chinese'|'english'|'maths'|'myclass')} request.server - AI server
 * @param {string} request.endpoint - API endpoint
 * @param {('GET'|'POST'|'PUT'|'DELETE')} [request.method='POST'] - HTTP method
 * @param {Object} [request.data] - Request body data
 * @param {Object} [request.params] - Query parameters
 * @returns {Promise<Object>} AI server response
 */
export async function callAIProxy(request) {
  return callEdgeFunction('ai-proxy', {
    server: request.server,
    endpoint: request.endpoint,
    method: request.method || 'POST',
    data: request.data,
    params: request.params,
  });
}

/**
 * Manage API Keys
 * @param {string} action - 'create' | 'list' | 'get' | 'update' | 'delete' | 'test'
 * @param {Object} data - Key data
 * @returns {Promise<Object>} API key response
 */
export async function manageAPIKeys(action, data = {}) {
  return callEdgeFunction('api-keys', {
    action,
    ...data,
  });
}

/**
 * Execute workflow
 * @param {string} workflowId - Workflow ID
 * @param {Object} inputData - Input data
 * @param {boolean} testMode - Test mode flag
 * @returns {Promise<Object>} Execution result
 */
export async function executeWorkflow(workflowId, inputData = {}, testMode = false) {
  return callEdgeFunction('execute-workflow', {
    workflow_id: workflowId,
    input_data: inputData,
    test_mode: testMode,
  });
}

/**
 * Get analytics
 * @param {Object} query - Analytics query
 * @param {string} [query.type='usage'] - Analytics type
 * @param {string} [query.start_date] - Start date
 * @param {string} [query.end_date] - End date
 * @returns {Promise<Object>} Analytics data
 */
export async function getAnalytics(query = {}) {
  return callEdgeFunction('analytics', {
    type: query.type || 'usage',
    start_date: query.start_date,
    end_date: query.end_date,
    ...query,
  });
}

